import './popupSpese.scss';

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import useApiConfiguration from '../../apiConfig';
import { confirm } from 'devextreme/ui/dialog';
import { LavoriApi, LavoriDettaglioSpeseResponseDTO } from '../../data';
import { TabPanel, Button, CheckBox, FileUploader, LoadPanel, NumberBox, TextArea, Toast } from 'devextreme-react';
import { Popup, Position, ToolbarItem } from 'devextreme-react/popup';
import { useScreenSize } from '../../utils/media-query';
import { ScrollView } from 'devextreme-react/scroll-view';
import { ClickEvent } from 'devextreme/ui/button';
import { PopupPdfViewer } from '..';
import { PopupPdfViewerHandle } from '../pdfViewer/popupPdfViewer';

export type PopupSpeseHandle = {
    show: (idLavoro: number, commessa: string) => void
};

type Props = {
    onChange: () => void
};

type State = {
    idLavoro: number,
    commessa: string,
    visible: boolean,
    accessToken?: string
};

const PopupSpese: React.ForwardRefRenderFunction<PopupSpeseHandle, Props> = (props, ref) => {
    const apiConfiguration = useApiConfiguration();
    const pdfViewerRef = useRef<PopupPdfViewerHandle>(null);
    const { isXSmall, isSmall } = useScreenSize();
    const fileExtensions = ['.pdf', '.jpg', '.jpeg', '.gif', '.png'];

    const [toastConfig, setToastConfig] = useState({ isVisible: false, isOk: false, message: '' } as { isVisible: boolean, isOk: boolean, message: string });

    const [loading, setLoading] = useState(false);
    const [state, setState] = useState<State>({ idLavoro: 0, commessa: '', visible: false, accessToken: '' });
    const [dataSourceSpese, setDataSourceSpese] = useState<LavoriDettaglioSpeseResponseDTO[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);

    function hideInfo() {
        setState({ idLavoro: 0, commessa: '', visible: false, accessToken: '' });
    };

    useImperativeHandle(ref, () => ({
        async show(idLavoro: number, commessa: string) {
            setLoading(true);

            let token: string | undefined;
            if (typeof apiConfiguration.accessToken === 'function')
                token = await apiConfiguration.accessToken();
            else
                token = apiConfiguration.accessToken;

            setState({ idLavoro, commessa, visible: true, accessToken: token });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            const api = new LavoriApi(apiConfiguration);

            api.getSpeseByLavoroId({ idLavoro: state.idLavoro })
                .then((res) => {
                    setDataSourceSpese(res);
                }).finally(() => setLoading(false));
        }
    }, [state.visible]);

    const handleChange = useCallback((index: number, field: string, value: any) => {
        setDataSourceSpese(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    function getUrl() {
        return `${apiConfiguration.basePath}/api/Lavori/uploadFileSpese`;
    };

    const handleFileUploaded = useCallback((e: any, idTipoRimborso: number) => {
        try {
            const response = JSON.parse(e.request.response);

            const nuovoAllegato = {
                idFileAllegato: response.id,
                fileName: e.file.name,
            };

            setDataSourceSpese(prev => {
                const updated = prev.map(spesa => {
                    if (spesa.idTipoRimborso === idTipoRimborso) {
                        const allegatiAttuali = spesa.allegati ?? [];
                        return {
                            ...spesa,
                            allegati: [...allegatiAttuali, nuovoAllegato],
                        };
                    }
                    return spesa;
                });

                return updated;
            });

            setToastConfig({
                isVisible: true,
                isOk: true,
                message: 'File caricato correttamente',
            });
        } catch (err) {
            console.error('Errore parsing risposta upload', err);
            setToastConfig({
                isVisible: true,
                isOk: false,
                message: 'Errore durante l\'upload del file',
            });
        }
    }, []);

    function upsert(e: ClickEvent) {
        var api = new LavoriApi(apiConfiguration);
        api.upsertSpese({
            idLavoro: state.idLavoro,
            lavoriSpeseInsertDTO: dataSourceSpese.map(item => ({
                idTipoRimborso: item.idTipoRimborso,
                totale: item.valore ?? 0,
                nonAddebitabile: item.nonAddebitabile ?? false,
                sostenuteSocieta: item.sostenuteSocieta ?? false,
                note: item.note ?? null
            }))
        }).then(() => {
            props.onChange();
            hideInfo();
        });
    };

    const showAllegato = async (idAllegato: number) => {
        setLoading(true);

        var api = new LavoriApi(apiConfiguration);
        const response = await api.getFileSpesaById({ idFileAllegato: idAllegato });
        if (response?.base64) {
            const fileContent = atob(response.base64);
            const byteArray = new Uint8Array(fileContent.length);
            for (let i = 0; i < fileContent.length; i++)
                byteArray[i] = fileContent.charCodeAt(i);

            const mimeType = "application.pdf";
            const fileBlob = new Blob([byteArray], { type: mimeType });

            if (fileBlob)
                pdfViewerRef.current?.show(fileBlob);
        }

        setLoading(false);
    };

    function checkDelete(id: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deleteAllegato(id);
        });
    };

    const deleteAllegato = async (idAllegato: number) => {
        setLoading(true);
        var api = new LavoriApi(apiConfiguration);

        api.deleteAllegatoSpesaRaw({ idFileAlegato: idAllegato })
            .then(() => {
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    isOk: true,
                    message: 'Cancellazione completata'
                });

                setDataSourceSpese(prev => {
                    const updated = prev.map(spesa => {
                        if (!spesa.allegati) return spesa;

                        const newAllegati = spesa.allegati.filter(a => a.idFileAllegato !== idAllegato);
                        return { ...spesa, allegati: newAllegati };
                    })

                    return updated;
                });
            })
            .catch((error) => {
                console.error(error);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    isOk: false,
                    message: 'Errore durante la cancellazione'
                });
            })
            .finally(() => setLoading(false));
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    const itemRender = useCallback((item: LavoriDettaglioSpeseResponseDTO) => {
        const index = dataSourceSpese.findIndex(r => r.idTipoRimborso === item.idTipoRimborso);

        return (
            <div className='valoriSpese' key={item.idTipoRimborso}>
                <NumberBox labelMode='floating' label='Valore'
                    value={item.valore} onValueChanged={(e) => handleChange(index, 'valore', e.value)}
                />

                <div className='checkbox-row'>
                    <CheckBox text='Non addebitabile' value={item.nonAddebitabile ?? false}
                        onValueChanged={(e) => handleChange(index, 'nonAddebitabile', e.value)}
                    />
                    <CheckBox text='Sostenuto dalla società' value={item.sostenuteSocieta ?? false}
                        onValueChanged={(e) => handleChange(index, 'sostenuteSocieta', e.value)}
                    />
                </div>

                <TextArea maxLength={250} autoResizeEnabled={true} labelMode='floating' label='Note'
                    value={item.note} onValueChanged={(e) => handleChange(index, 'note', e.value)}
                />

                {
                    item.allegati?.length > 0 && (
                        <div className='file-section'>
                            <div className='file-section-caption'>
                                Documenti già caricati
                            </div>

                            <div className='file-list'>
                                {item.allegati?.map((f, i) => (
                                    <div key={i} className='file-item'>
                                        <Button
                                            icon="trash"
                                            hint='Elimina allegato'
                                            stylingMode='text'
                                            onClick={() => checkDelete(f.idFileAllegato)}
                                        />
                                        <Button
                                            icon="pdffile"
                                            hint="Apri file"
                                            stylingMode='text'
                                            onClick={() => showAllegato(f.idFileAllegato)}
                                        />
                                        <span>{f.fileName}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                }

                <FileUploader className='fileUploader' multiple={true} allowedFileExtensions={fileExtensions}
                    uploadMode='instantly' name="File" uploadUrl={getUrl()}
                    uploadHeaders={{ 'Authorization': state.accessToken }}
                    uploadCustomData={{ 'idLavoro': state.idLavoro, 'idTipoSpesa': item.idTipoRimborso }}
                    onUploaded={(e) => {
                        handleFileUploaded(e, item.idTipoRimborso);
                        e.component.reset();
                    }}
                />
            </div>
        )
    }, [dataSourceSpese, handleChange, fileExtensions, getUrl, state.accessToken, state.idLavoro]);

    return (
        <React.Fragment>
            <Popup
                visible={state.visible} showTitle={true} showCloseButton={true}
                dragEnabled={false} fullScreen={(isXSmall || isSmall)}
                hideOnOutsideClick={true} onHidden={hideInfo} title='Rimborso spese'
                container='.dx-viewport'
            >
                <ToolbarItem widget='dxButton' location='center' toolbar='bottom'>
                    <Button
                        onClick={upsert}
                        icon='check'
                        text='Salva'
                        type='default'
                        stylingMode='contained'
                    />
                </ToolbarItem>

                {loading && <LoadPanel style={{ zIndex: 9999 }} position={'center'} visible shading shadingColor='rgba(0, 0, 0, 0.4)' />}

                <ScrollView>
                    <Position at="center" my="center" of="" collision="fit" />

                    <div className="popup-spese-subtitle">
                        {state.commessa}
                    </div>

                    <TabPanel
                        className='tabpanel-spese'
                        items={dataSourceSpese}
                        keyExpr="idTipoRimborso"
                        selectedIndex={selectedIndex}
                        onSelectionChanged={(e) => setSelectedIndex(e.component.option('selectedIndex'))}
                        tabsPosition={(isXSmall || isSmall) ? 'top' : 'left'}
                        swipeEnabled showNavButtons animationEnabled
                        deferRendering={false}
                        focusStateEnabled={false}
                        itemTitleRender={(item: LavoriDettaglioSpeseResponseDTO) => {
                            return <span className='tab-title'>{item.descrizione}</span>
                        }}
                        itemRender={itemRender}
                        height='100%'
                    />
                </ScrollView>

                <PopupPdfViewer ref={pdfViewerRef} />
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type={toastConfig.isOk ? "success" : "error"}
                onHiding={onHidingToast} closeOnClick={true}
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />
        </React.Fragment>
    )
}

export default React.forwardRef(PopupSpese);